import EChartCommon, {
  getAxisPointer,
} from '@/pages/opinion/components/EChartCommon';
import { echartsContent } from '@/pages/opinion/utils';
import starBlue from '@/pages/opinion/assets/svg/starblue.svg';
import React, { useMemo, useRef, useState } from 'react';
import {
  formatTimeXAxisText,
  getChartPosition,
} from '@/pages/opinion/utils/chart';
import { useIntl } from 'umi';

export default function ScoreTrendCharts({
  platformData,
  filterParams,
  tooltipFormatter,
  onClick,
  height = 248,
  tooltipBottom = -50,
}: any) {
  // 检验当前数值是否为空
  const [isNull, setIsNull] = useState(false);
  const chartRef = useRef(null);
  const { formatMessage } = useIntl();
  const initMentions = (data: any[]) => {
    let option: any = {
      tooltip: {
        formatter: (params: any) => tooltipFormatter(params),
        position: (
          pos: [any, any],
          params: any,
          dom: any,
          rect: any,
          size: { viewSize: [any, any] },
        ) => getChartPosition(pos, params, dom, rect, size, tooltipBottom, 20, 'top'),
        appendToBody: false,
        confine: false,
      },
      axisPointer: getAxisPointer((data) =>
        formatTimeXAxisText(data.value, filterParams?.date_type),
      ),
      grid: {
        top: 20,
        right: 0,
        containLabel: true,
      },
      legend: {
        data: [
          {
            name: 'Lifetime User Score',
            itemStyle: {
              color: '#5086F3',
            },
            textStyle: {
              color: '#888',
            },
          },
          {
            name: 'Selected User Score',
            itemStyle: {
              color: '#6A7597',
            },
            textStyle: {
              color: '#888',
            },
          },
          {
            name: '1星',
            itemStyle: {
              color: '#E95F6A',
            },
          },
          {
            name: '2星',
            itemStyle: {
              color: '#F6BFC3',
            },
          },
          {
            name: '3星',
            itemStyle: {
              color: '#B8EACF',
            },
          },
          {
            name: '4星',
            itemStyle: {
              color: '#95E0B7',
            },
          },
          {
            name: '5星',
            itemStyle: {
              color: '#4FCB87',
            },
          },
        ],
        formatter: (params: string) => {
          const num = parseInt(params[0]);
          if (isNaN(num)) return formatMessage({ id: params });

          let legendIcon = '';
          for (let index = 0; index < num; index++) {
            legendIcon += `{databrainImg|}{space|}`;
          }
          return legendIcon;
        },
        textStyle: {
          rich: {
            a: {
              color: '#888888',
              fontSize: 12,
            },
            databrainImg: {
              height: 10,
              width: 10,
              backgroundColor: {
                image: starBlue,
              },
            },
            space: {
              width: 2,
            },
          },
        },
      },
      xAxis: {
        boundaryGap: ['20%', 0],
        data: data?.map?.((item) => item?.time),
        axisLabel: {
          showMaxLabel: true,
          formatter: (value: string) =>
            formatTimeXAxisText(value, filterParams?.date_type),
        },
      },
      yAxis: [
        {
          // name: formatMessage({ id: 'Reviews' }),
          axisLabel: {
            color: '#838697',
            formatter: (value: any) => {
              return echartsContent(value);
            },
          },
          splitLine: {
            show: true,
            lineStyle: { color: '#E5E5E5', type: 'dashed' },
          },
          axisTick: { show: false },
          type: 'value',
        },
        {
          type: 'value',
          axisLabel: {
            color: '#838697',
            formatter: (value: any) => {
              return echartsContent(value);
            },
          },
          splitLine: {
            show: false,
          },
          axisTick: { show: false },
        },
      ],
      color: ['#E95F6A', '#F6BFC3', '#B8EACF', '#95E0B7', '#4FCB87'],
      series: [],
    };
    // 线型图tooltip
    // option.tooltip.formatter = (params: any) => {
    //   return noValueEchartsTooltip(params);
    // };
    let seriesOneStarData: any[] = [];
    let seriesTwoStarData: any[] = [];
    let seriesThreeStarData: any[] = [];
    let seriesFourStarData: any[] = [];
    let seriesFiveStarData: any[] = [];
    let lifetimeData: any[] = [];
    let selectedData: any[] = [];
    let seriesTotal: any[] = []; // reviews总和
    const offsetWidth: number = chartRef?.current?.offsetWidth;
    data?.forEach((element, i) => {
      seriesOneStarData.push(element?.star1);
      seriesTwoStarData.push(element?.star2);
      seriesThreeStarData.push(element?.star3);
      seriesFourStarData.push(element?.star4);
      seriesFiveStarData.push(element?.star5);
      lifetimeData.push(element?.lifetime_score);
      selectedData.push(element?.selected_score);
      const totalReview =
        data[i]?.star1 +
        data[i]?.star2 +
        data[i]?.star3 +
        data[i]?.star4 +
        data[i]?.star5;
      seriesTotal.push(totalReview === 0 ? '' : totalReview);
    });
    // setIsNull(seriesTotal?.every((t) => t === ''));

    // 小屏1366px且柱子数大于10，或者柱子数大于16根，不显示汇总值
    if ((offsetWidth <= 518 && data?.length > 10) || data?.length > 16) {
      seriesTotal = [];
    }

    option.series = [
      {
        name: 'total总数',
        type: 'bar',
        barMaxWidth: 50, // 柱子宽度同实体柱子，否则会错位
        barGap: '-100%', // 左移100%，stack不再与需堆叠的柱子在一列
        label: {
          normal: {
            show: true,
            position: 'top', //  位置设为top
            formatter: (c) => echartsContent(c?.value),
            textStyle: { color: '#444' },
          },
        },
        itemStyle: {
          normal: {
            color: 'rgba(128, 128, 128, 0)', // 为透明
          },
        },
        data: seriesTotal,
      },
      {
        name: '5星',
        type: 'bar',
        stack: 'time',
        barMaxWidth: 50,
        itemStyle: {
          color: '#4FCB87',
        },
        data: seriesFiveStarData,
      },
      {
        name: '4星',
        type: 'bar',
        stack: 'time',
        barMaxWidth: 50,
        itemStyle: {
          color: '#95E0B7',
        },
        data: seriesFourStarData,
      },
      {
        name: '3星',
        type: 'bar',
        stack: 'time',
        barMaxWidth: 50,
        itemStyle: {
          color: '#B8EACF',
        },
        data: seriesThreeStarData,
      },
      {
        name: '2星',
        type: 'bar',
        stack: 'time',
        barMaxWidth: 50,
        itemStyle: {
          color: '#F6BFC3',
        },
        data: seriesTwoStarData,
      },
      {
        name: '1星',
        type: 'bar',
        stack: 'time',
        barMaxWidth: 50,
        itemStyle: {
          color: '#E95F6A',
        },
        data: seriesOneStarData,
      },
      {
        name: 'Lifetime User Score',
        type: 'line',
        yAxisIndex: 1,
        itemStyle: {
          color: '#5086F3',
        },
        data: lifetimeData,
      },
      {
        name: 'Selected User Score',
        type: 'line',
        yAxisIndex: 1,
        itemStyle: {
          color: '#6A7597',
        },
        data: selectedData,
      },
    ];

    return option;
  };

  const options = useMemo(() => {
    if (platformData && filterParams) {
      return initMentions(platformData);
    }
    return {};
  }, [platformData, formatMessage]);

  return (
    <div ref={chartRef}>
      <EChartCommon
        empty={isNull}
        height={ height}
        options={options}
        leftTitle={formatMessage({ id: 'Reviews' })}
        rightTitle={formatMessage({ id: 'User Score' })}
        optionsProps={{ type: 'bar' }}
        eventsDict={{ click: onClick }}
      ></EChartCommon>
    </div>
  );
}
